import { useState, useEffect, useReducer, Fragment } from "react";
import { axiosPost, axiosPut } from "@/services/axiosService";
import useTelegramConfig from "@/pages/omni-builder/react-query/configs/useTelegramConfig";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import NoResultFound from "@/components/shared/noResultFound/noResultFound";
import { useDispatch, useSelector } from "react-redux";
import { State } from "@/redux/Types";
import { userSlice } from "@/redux/User";
import { TelegramIcon } from "@/pages/cbb-builder/components/AiBotAssets";
import { getAccounts } from "@/services/googlepicker.service";
import BooleanModal from "@/components/builder/BooleanModal";
import {
  ConnectIcon,
  DisconnectIcon,
} from "@/pages/cbb-builder/components/Channels/components/WhatsappChannel";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";
import { AnimatePresence, motion } from "framer-motion";
import { ErrSvgIcon } from "./Instagram";
interface Props {
  onClose?: any;
  botCustomId?: any;
  liveBotId?: any;
  // fromBuilder?: any;
  // getHandoffConfigs?: any;
  onSelect?: any;
}
export default function Telegram({ onClose, botCustomId, liveBotId }: Props) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const [isExist, setIsExist] = useState<any>({});
  const [botToken, setBotToken] = useState("");
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountsLoader, setAccountsLoader] = useState<boolean>(true);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [showLoginLogoutPrompt, setShowLoginLogoutPrompt] =
    useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState({});
  const [showAccount, setShowAccount] = useState(false);
  const [loader, setLoader] = useState(false);
  const [reload, fReload] = useReducer((state) => !state, false);
  const telegramConfigData = useTelegramConfig();
  const [connection, setConnection] = useState<any>(false);
  const { isError, isFetching, isLoading, data } = telegramConfigData ?? {};

  useEffect(() => {
    if (isFetching || isLoading) {
      setAccountsLoader(true);
      return;
    }

    if (isError) {
      console.error("Error:", telegramConfigData.error || "Unknown error");
      onClose();
      return;
    }

    if (data) {
      setAccounts(data);
    }

    setAccountsLoader(false);
  }, [isError, isFetching, isLoading, data]);
  const addAccount = async () => {
    if (!botToken)
      return await _sendErrorToast(
        "The bot token cannot be empty. Please enter it to continue.",
      );
    try {
      setConnection(true);
      const res = await axiosPost("/configurations/setAppConfig", {
        siteName: "telegram",
        botToken,
        liveBotId,
        botCustomId,
      });

      if (res.data.success) {
        queryClient.invalidateQueries({
          queryKey: ["configurations", "telegram"],
        });
        await _sendSuccessToast(
          res.data.message || "Configuration saved successfully.",
        );
        setBotToken("");
        onClose();
      } else if (!res.data.success && res.data.message === "Already exist") {
        setShowLoginLogoutPrompt(true);
      } else {
        await _sendErrorToast(
          res.data.message || "An error occurred. Please try again.",
        );
        setBotToken("");
        onClose();
      }
    } catch (err) {
      console.error("Network or unexpected error:", err);
      await _sendErrorToast("Network or unexpected error");
    } finally {
      setShowAccount(false);

      setConnection(false);
      // onClose();
    }
  };

  const handleLink = async (data, type) => {
    try {
      if (!liveBotId || liveBotId === undefined || liveBotId === "undefined") {
        return await _sendErrorToast("Bot is not live yet!");
      }
      setConnection(true);
      setConnectionStatus((prev) => ({ ...prev, [data._id]: true }));
      let isChanged = false;
      if (type === "connect") {
        const shouldUpdateState = accounts?.some(
          (item) =>
            item._id === data._id &&
            data.botCustomId &&
            data.botCustomId !== botCustomId,
        );

        if (shouldUpdateState) {
          isChanged = true;
          setIsExist((prev) => ({
            ...prev,
            [data._id]: true,
          }));
        }
      }
      if (isChanged) return;

      const res = await axiosPut(
        `/configurations/${type === "disconnect" ? "disconnectAcccount" : "connectAcccount"}`,
        {
          siteName: "telegram",
          _id: data._id,
          botId: liveBotId,
          botCustomId,
          botToken: data.botToken,
        },
      );
      if (res.data?.success) {
        fReload();
        const actionText = type === "replace" ? "replaced" : type;
        await _sendSuccessToast(
          `Your telegram bot ${actionText}d successfully`,
        );
        queryClient.invalidateQueries({
          queryKey: ["configurations", "telegram"],
        });
      } else {
        await _sendErrorToast(
          res.data?.message || "An error occurred. Please try again.",
        );
      }
      if (type === "replace") {
        setIsExist((prev) => ({
          ...prev,
          [data._id]: false,
        }));
      }
      onClose();
    } catch (err) {
      console.error("An unexpected error occurred:", err);
      await _sendErrorToast(
        "Failed to process the request. Please check your connection or try again.",
      );
    } finally {
      setConnection(false);
      setConnectionStatus((prev) => ({ ...prev, [data._id]: false }));
    }
  };

  async function _sendErrorToast(err?: any) {
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "error",
        message: !!err
          ? typeof err === "string"
            ? err
            : typeof err?.response?.data?.message === "string"
              ? err?.response?.data?.message
              : "Something went wrong"
          : "Something went wrong",
      }),
    );
  }

  async function _sendSuccessToast(msg?: any) {
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: msg,
      }),
    );
  }

  const deleteBot = async (id) => {
    try {
      if (connection) return;
      setConnection(true);
      const res = await axiosPut(
        `/configurations/deleteAppConfig?id=${id}&siteName=telegram`,
      );
      if (res.data.success) {
        await _sendSuccessToast(`Bot deleted successfully`);
      } else {
        await _sendErrorToast("Bot not exist anymore");
      }
      queryClient.invalidateQueries({
        queryKey: ["configurations", "telegram"],
      });
    } catch (err) {
      await _sendErrorToast(
        "Failed to process the request. Please check your connection or try again.",
      );
    } finally {
      setConnection(false);
    }
  };

  const handleLoginLogoutPopup = async () => {
    try {
      setConnection(true);
      const res = await axiosPost("/configurations/setAppConfig?forced=true", {
        siteName: "telegram",
        botToken,
        liveBotId,
        botCustomId,
      });
      if (res.data.success) {
        queryClient.invalidateQueries({
          queryKey: ["configurations", "telegram"],
        });
        await _sendSuccessToast(
          res.data.message || "Configuration saved successfully.",
        );
      } else {
        await _sendErrorToast(
          res.data.message || "An error occurred. Please try again.",
        );
      }
    } catch (err) {
      console.error("Network or unexpected error:", err);
      await _sendErrorToast("Network or unexpected error");
    } finally {
      setShowAccount(false);
      setConnection(false);
      setShowLoginLogoutPrompt(false);
      setBotToken("");
      onClose();
    }
  };

  if (showLoginLogoutPrompt) {
    return (
      <ModalWrapper>
        <BooleanModal
          onConfirm={() => handleLoginLogoutPopup()}
          onCancel={() => setShowLoginLogoutPrompt(false)}
          content={{
            title: `Telegram account already connected to ChatbotBuilder`,
            message: `This Telegram account is already connected to another company. Do you want to logout from the previous company and login here?`,
          }}
        />
      </ModalWrapper>
    );
  }

  return (
    <>
      {telegramConfigData?.data && (
        <div className="mt-[10px]">
          <h4 className="mb-[10px] text-[16px] font-medium text-text-clr max-[1280px]:!text-[14px]">
            Connected Bots
          </h4>
          <div className="fb-pages skeletonPages rounded-[20px] bg-[#fafafa] p-[20px]">
            <h3 className="text-[14px] !font-normal leading-[110%] !text-[#666] max-[1280px]:!text-[13px]">
              Details
            </h3>

            {!showAccount &&
              (accountsLoader ? (
                <div className="fb-list whatsapp_table overflow-x-auto">
                  <table className="mt-[10px] min-w-full overflow-hidden rounded-[10px] border border-[rgba(167,80,80,0.1)]">
                    <thead className="flex w-full rounded-t-[10px] border border-[rgba(0,0,0,0.10)] bg-gray-100 text-gray-700">
                      <tr className="grid">
                        <th className="px-[20px] py-[13px]">Bot Name</th>
                        <th className="px-[20px] py-[13px]">User Name</th>
                        <th className="px-[20px] py-[13px]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(4)].map((_, index) => (
                        <tr key={index} className="grid border-b">
                          <td className="flex items-center gap-[8px] px-[20px] py-[13px]">
                            <Skeleton
                              circle
                              height={20}
                              width={20}
                              className="flex-shrink-0"
                            />
                            <Skeleton width={120} height={16} />
                          </td>
                          <td className="px-[20px] py-[13px]">
                            <Skeleton width={100} height={16} />
                          </td>
                          <td className="flex items-center justify-between gap-2 px-[20px] py-[13px]">
                            <Skeleton
                              width={90}
                              height={30}
                              className="rounded-[10px]"
                            />
                            <Skeleton
                              width={24}
                              height={24}
                              className="rounded"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                accounts.length > 0 && (
                  <div className="fb-list whatsapp_table overflow-x-auto">
                    <table className="mt-[10px] min-w-full overflow-hidden rounded-[10px] border border-[rgba(167,80,80,0.1)]">
                      <thead className="flex w-full rounded-t-[10px] border border-[rgba(0,0,0,0.10)] bg-gray-100 text-gray-700">
                        <tr className="grid">
                          <th className="px-[20px] py-[13px]">Bot Name</th>
                          <th className="px-[20px] py-[13px]">User Name</th>
                          <th className="px-[20px] py-[13px]">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts?.map((account, index) => (
                          <Fragment key={index}>
                            <tr
                              className={`grid ${
                                data?.showPrompt ? "block" : "table-row"
                              } border-b`}
                            >
                              <td className="flex items-center gap-[8px] px-[20px] py-[13px]">
                                {account?.picture ? (
                                  <img
                                    src={account?.picture}
                                    alt={account?.name}
                                    height={10}
                                    width={20}
                                  />
                                ) : (
                                  <UserIcon />
                                )}
                                {account?.name}
                                {account.connected &&
                                  account?.botCustomId === botCustomId && (
                                    <span className="rounded-[5px] bg-[#3395dd] px-[6px] py-[4px] text-[12px] leading-[100%] text-white">
                                      Connected
                                    </span>
                                  )}
                              </td>
                              <td className="break-all px-[20px] py-[13px]">
                                @{account.userName}
                              </td>
                              <td className="justify-between px-[20px] py-[13px]">
                                {account.connected &&
                                account?.botCustomId === botCustomId ? (
                                  <button
                                    disabled={!!connectionStatus[account._id]}
                                    className="social_disconnect flex h-[30px] items-center gap-[8px] rounded-[10px] border border-solid border-[rgba(251,95,102,0.1)] bg-[#FEE6E7] px-[15px] py-[10px] text-[14px] leading-[110%] text-primary-clr"
                                    onClick={() => {
                                      handleLink(account, "disconnect");
                                      // setShowWarning(true);
                                      // setIsExist((prev) => {
                                      //   const updated = {
                                      //     ...prev,
                                      //     [account._id]: true,
                                      //   };
                                      //   return updated;
                                      // });
                                    }}
                                  >
                                    <DisconnectIcon />{" "}
                                    {connectionStatus[account._id]
                                      ? "Disconnecting..."
                                      : "Disconnect"}
                                  </button>
                                ) : (
                                  <button
                                    disabled={!!connectionStatus[account._id]}
                                    className="border-[rgba(0, 0, 0, 0.10)] social_connect flex h-[30px] items-center gap-[8px] rounded-[10px] border border-solid bg-[#F9F9F9] px-[15px] py-[10px] text-[14px] leading-[110%] text-[#666]"
                                    onClick={() => {
                                      handleLink(account, "connect");
                                    }}
                                  >
                                    <ConnectIcon />{" "}
                                    {connectionStatus[account._id]
                                      ? "Connecting..."
                                      : "Connect"}
                                  </button>
                                )}

                                <button
                                  disabled={connection}
                                  style={{
                                    border: "none",
                                    background: "none",
                                  }}
                                  className="fs-0 cursor-pointer"
                                  onClick={() => deleteBot(account._id)}
                                >
                                  <DeleteBot />
                                </button>
                              </td>
                            </tr>

                            {account?.connected && isExist[account._id] && (
                              <ModalWrapper>
                                {/* <AnimatePresence> */}
                                <div className="createbot-modal">
                                  <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                  >
                                    <div className="flex-cnt mx-4 w-[680px] flex-col rounded-[10px] bg-[linear-gradient(180deg,_#FB5F66_-51.8%,_#FFFFFF_70.59%)] p-[40px]">
                                      <ErrSvgIcon />
                                      <h2 className="mb-[10px] mt-[20px] text-[18px] font-medium text-text-clr">
                                        Replace Bot
                                      </h2>
                                      <p className="mb-[30px] text-center text-[14px] text-[#444444CC]">
                                        This account is already connected to
                                        another bot. <br />
                                        Do you want to replace the earlier bot
                                        with current bot?
                                      </p>

                                      <div className="flex justify-end gap-4">
                                        <motion.button
                                          whileHover={{
                                            scale: 1.1,
                                            transition: { duration: 0.5 },
                                          }}
                                          whileTap={{ scale: 0.9 }}
                                          onClick={() => {
                                            setIsExist((prev) => {
                                              const updated = {
                                                ...prev,
                                                [account._id]: false,
                                              };
                                              return updated;
                                            });
                                          }}
                                          disabled={isLoading}
                                          className="h-[45px] w-[170px] rounded-[6px] border border-primary-clr text-[14px] text-primary-clr"
                                        >
                                          Cancel
                                        </motion.button>
                                        <motion.button
                                          whileHover={{
                                            scale: 1.1,
                                            transition: { duration: 0.5 },
                                          }}
                                          whileTap={{ scale: 0.9 }}
                                          onClick={() =>
                                            handleLink(account, "replace")
                                          }
                                          disabled={isLoading || connection}
                                          className="w-[170px] rounded-[6px] border border-primary-clr bg-primary-clr text-[14px] text-white"
                                        >
                                          Replace Bot
                                        </motion.button>
                                      </div>
                                    </div>
                                  </motion.div>
                                </div>
                                {/* </AnimatePresence> */}
                              </ModalWrapper>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ))}

            {accounts.length ? (
              <span
                style={{ color: "#FB5F66" }}
                onClick={() => setShowAccount(true)}
                className="mt-15 flex cursor-pointer items-center gap-[8px] !text-[13px] hover:underline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={13}
                  height={13}
                  viewBox="0 0 13 13"
                  fill="none"
                >
                  <path
                    d="M5.64045 12.5C5.49064 12.5 5.3633 12.4551 5.25843 12.3652C5.16854 12.2603 5.1236 12.133 5.1236 11.9831V7.30899H0.516854C0.367041 7.30899 0.2397 7.26405 0.134831 7.17416C0.0449438 7.06929 0 6.94195 0 6.79214V6.07303C0 5.92322 0.0449438 5.80337 0.134831 5.71348C0.2397 5.60861 0.367041 5.55618 0.516854 5.55618H5.1236V1.01685C5.1236 0.867041 5.16854 0.747191 5.25843 0.657304C5.3633 0.552435 5.49064 0.5 5.64045 0.5H6.42697C6.57678 0.5 6.69663 0.552435 6.78652 0.657304C6.89139 0.747191 6.94382 0.867041 6.94382 1.01685V5.55618H11.573C11.7228 5.55618 11.8427 5.60861 11.9326 5.71348C12.0375 5.80337 12.0899 5.92322 12.0899 6.07303V6.79214C12.0899 6.94195 12.0375 7.06929 11.9326 7.17416C11.8427 7.26405 11.7228 7.30899 11.573 7.30899H6.94382V11.9831C6.94382 12.133 6.89139 12.2603 6.78652 12.3652C6.69663 12.4551 6.57678 12.5 6.42697 12.5H5.64045Z"
                    fill="#FB5F66"
                  />
                </svg>
                Add new bot
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
      )}

      {((!accountsLoader && !accounts.length) || showAccount) && (
        <ModalWrapper>
          <div className="general-container telegram_modal">
            <div
              className="overflow"
              onClick={() => {
                showAccount ? setShowAccount(false) : onClose();
              }}
            >
              <div
                className="no-data-add-button integration-content flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-head">
                  <h2>Connect Telegram bot</h2>
                  <TelegramIcon />
                  <div
                    className="closing-modal"
                    onClick={() => {
                      showAccount ? setShowAccount(false) : onClose();
                    }}
                  >
                    <button>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
                          fill="#6D6D6D"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="modal-content-box flex h-full flex-col rounded-[10px] bg-[#fff] !p-[0px]">
                  <div className="forMap h-full p-[30px]">
                    <div className="modal-content-head">
                      <h1 className="!text-[16px]">
                        Connect Telegram bot with
                        {fromOutgrow ? " Outgrow" : " Chatbot Builder"}
                      </h1>
                    </div>

                    <div className="fb-input fb-pages skeletonPages telegram_body">
                      <h3 className="!mb-[8px] !text-[12px] !font-normal leading-none !text-[#666]">
                        BOT TOKEN
                      </h3>
                      <input
                        className="h-[40px] !rounded-[8px] !border !border-[rgba(0,0,0,0.2)] !bg-[#FAFAFA] px-[15px] py-[10px]"
                        type="text"
                        value={botToken}
                        placeholder="Enter BOT token here"
                        onChange={(e) => setBotToken(e.target.value)}
                      />
                      <div className="instructions mt-[25px]">
                        <h3 className="!mb-[8px] !text-[12px] !font-normal leading-none !text-[#666]">
                          INSTRUCTIONS
                        </h3>
                        <ul>
                          <li className="flex items-center">
                            {" "}
                            <span>STEP 1</span>{" "}
                            <p className="flex items-center">
                              Open Telegram and search for &nbsp;{" "}
                              <strong>@BotFather.</strong>
                            </p>{" "}
                          </li>
                          <li className="flex items-center">
                            {" "}
                            <span>STEP 2</span>{" "}
                            <p className="flex items-center">
                              Start a chat with BotFather and send the command
                              &nbsp;
                              <strong>/newbot.</strong>
                            </p>{" "}
                          </li>
                          <li className="flex items-center">
                            {" "}
                            <span>STEP 3</span>{" "}
                            <p className="flex items-center">
                              Follow the prompts to set your bot's name and
                              username (username should end with &nbsp;{" "}
                              <strong>bot</strong>).
                            </p>{" "}
                          </li>
                          <li className="flex items-center">
                            {" "}
                            <span>STEP 4</span>{" "}
                            <p className="flex items-center">
                              Copy the bot token provided by BotFather.
                            </p>{" "}
                          </li>
                          <li className="flex items-center">
                            {" "}
                            <span>STEP 5</span>{" "}
                            <p className="flex items-center">
                              Send the command &nbsp;{" "}
                              <strong>/setprivacy</strong> &nbsp; to disable
                              privacy mode for your bot (for sending messages).
                            </p>{" "}
                          </li>
                          {/* <li className="flex items-center">
                              {" "}
                              <span>STEP 6</span>{" "}
                              <p className="flex items-center">
                                Click on 'Add New Telegram Bot'.
                              </p>{" "}
                            </li>
                            <li className="flex items-center">
                              {" "}
                              <span>STEP 7</span>{" "}
                              <p className="flex items-center">
                                {" "}
                                Paste your bot token.
                              </p>{" "}
                            </li> */}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="gap-30 flex h-[82px] items-center justify-center gap-[20px] rounded-b-[10px] border-t border-t-[rgba(0,0,0,0.10)] bg-[#F4F4F4] px-[30px] py-[20px]">
                    <button
                      disabled={connection}
                      className="cncl-btn !flex !h-[42px] !items-center !justify-center !rounded-[6px] !bg-[#FB5F66] !px-[46px] text-[14px] font-medium text-white"
                      onClick={addAccount}
                    >
                      {connection ? "Connecting..." : "Connect"}
                    </button>
                    {/* {showAccount && (
                      <button
                        className="cncl-btn !flex !h-[42px] !items-center !justify-center !rounded-[6px] border border-primary-clr !bg-[#ffff] !px-[46px] text-[14px] font-medium text-[#fb5f66]"
                        disabled={connection}
                        onClick={() => setShowAccount(false)}
                      >
                        Cancel
                      </button>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalWrapper>
      )}
      {/* 
      {showLoginLogoutPrompt && (
        <ModalWrapper>
          <BooleanModal
            onConfirm={() => handleLoginLogoutPopup()}
            onCancel={() => setShowLoginLogoutPrompt(false)}
            content={{
              title: `Telegram account already connected to ChatbotBuilder}`,
              message: `This Telegram account is already connected to another company. Do you want to logout from the previous company and login here?`,
            }}
          />
        </ModalWrapper>
      )} */}
    </>
  );
}

export const UserIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="44" height="44" rx="22" fill={"#D6D5FF"} />
      <path
        d="M16.3179 15.5312C16.3321 14.7896 16.4925 14.0581 16.7899 13.3786C17.0873 12.6991 17.5158 12.0849 18.0509 11.5712C18.586 11.0576 19.2171 10.6545 19.9082 10.3851C20.5993 10.1157 21.3368 9.98529 22.0784 10.0013C25.2744 10.0536 27.7351 12.6111 27.6751 15.8149C27.616 18.9116 25.0182 21.4143 21.9112 21.3646C18.7426 21.3149 16.257 18.7222 16.3179 15.5312ZM30.6638 26.292C28.4954 28.5336 26.3172 30.7646 24.1294 32.985C22.7906 34.3409 21.2101 34.3332 19.8851 32.9918C17.6949 30.772 15.5142 28.545 13.3429 26.3108C12.6144 25.5626 12.3907 24.6755 12.7892 23.6813C13.1621 22.7496 13.9557 22.2508 15.0468 22.25C17.3712 22.2465 19.6957 22.2465 22.0201 22.25C24.3239 22.25 26.6286 22.2611 28.9325 22.2448C30.0047 22.238 30.7804 22.6896 31.2021 23.6616C31.6237 24.6335 31.3898 25.5412 30.6638 26.292Z"
        fill={"#706AFF"}
      />
    </svg>
  );
};

export const DeleteBot = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={30}
    height={30}
    viewBox="0 0 30 30"
    fill="none"
    className="delete-bot-svg"
  >
    <rect width={30} height={30} rx={7.60096} fill="#FFEDEE" />
    <path
      d="M20.4383 10.664C20.2321 10.664 20.0344 10.7459 19.8886 10.8917C19.7428 11.0374 19.6609 11.2352 19.6609 11.4413V20.1404C19.6386 20.5335 19.4619 20.9019 19.1693 21.1653C18.8766 21.4286 18.4918 21.5657 18.0985 21.5466H11.8954C11.5022 21.5657 11.1173 21.4286 10.8246 21.1653C10.532 20.9019 10.3553 20.5335 10.333 20.1404V11.4413C10.333 11.2352 10.2511 11.0374 10.1053 10.8917C9.95952 10.7459 9.7618 10.664 9.55564 10.664C9.34948 10.664 9.15176 10.7459 9.00598 10.8917C8.86021 11.0374 8.77831 11.2352 8.77831 11.4413V20.1404C8.8005 20.9459 9.141 21.7098 9.72524 22.2648C10.3095 22.8197 11.0898 23.1205 11.8954 23.1013H18.0985C18.9041 23.1205 19.6844 22.8197 20.2687 22.2648C20.8529 21.7098 21.1934 20.9459 21.2156 20.1404V11.4413C21.2156 11.2352 21.1337 11.0374 20.9879 10.8917C20.8422 10.7459 20.6444 10.664 20.4383 10.664ZM21.2156 8.33199H18.1063V6.77733C18.1063 6.57117 18.0244 6.37345 17.8786 6.22768C17.7328 6.0819 17.5351 6 17.329 6H12.665C12.4588 6 12.2611 6.0819 12.1153 6.22768C11.9695 6.37345 11.8876 6.57117 11.8876 6.77733V8.33199H8.77831C8.57215 8.33199 8.37443 8.41389 8.22865 8.55967C8.08287 8.70545 8.00098 8.90316 8.00098 9.10933C8.00098 9.31549 8.08287 9.5132 8.22865 9.65898C8.37443 9.80476 8.57215 9.88666 8.77831 9.88666H21.2156C21.4218 9.88666 21.6195 9.80476 21.7653 9.65898C21.911 9.5132 21.9929 9.31549 21.9929 9.10933C21.9929 8.90316 21.911 8.70545 21.7653 8.55967C21.6195 8.41389 21.4218 8.33199 21.2156 8.33199ZM13.4423 8.33199V7.55466H16.5516V8.33199H13.4423Z"
      fill="#FB5F66"
    />
    <path
      opacity={0.5}
      d="M14.2187 18.4335V12.9922C14.2187 12.786 14.1368 12.5883 13.9911 12.4425C13.8453 12.2967 13.6476 12.2148 13.4414 12.2148C13.2352 12.2148 13.0375 12.2967 12.8917 12.4425C12.746 12.5883 12.6641 12.786 12.6641 12.9922V18.4335C12.6641 18.6397 12.746 18.8374 12.8917 18.9832C13.0375 19.1289 13.2352 19.2108 13.4414 19.2108C13.6476 19.2108 13.8453 19.1289 13.9911 18.9832C14.1368 18.8374 14.2187 18.6397 14.2187 18.4335ZM17.3281 18.4335V12.9922C17.3281 12.786 17.2462 12.5883 17.1004 12.4425C16.9546 12.2967 16.7569 12.2148 16.5507 12.2148C16.3446 12.2148 16.1468 12.2967 16.0011 12.4425C15.8553 12.5883 15.7734 12.786 15.7734 12.9922V18.4335C15.7734 18.6397 15.8553 18.8374 16.0011 18.9832C16.1468 19.1289 16.3446 19.2108 16.5507 19.2108C16.7569 19.2108 16.9546 19.1289 17.1004 18.9832C17.2462 18.8374 17.3281 18.6397 17.3281 18.4335Z"
      fill="#FB5F66"
    />
  </svg>
);
